import {
  DEPLOYMENT_UPDATE_PARTIAL_PREFIX,
  DEPLOYMENT_UPDATE_PREFIX,
} from 'constants/router';
import { Link, useLocation } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { Deployment } from 'interfaces/deployment';
import { useTranslation } from 'react-i18next';
import { TbArrowBack } from 'react-icons/tb';
import { FiSkipBack } from 'react-icons/fi';
import { useEffect, useState } from 'react';
import { PARTIAL_UPDATE_TYPE } from 'constants/deployment';
import { toast } from 'react-hot-toast';
import DeploymentService from 'services/DeploymentService';
import { ReactQueryKeys } from 'constants/react-query-keys';
import { useQuery } from '@tanstack/react-query';
import ReactTooltip from 'react-tooltip';
import downloadFile from 'helpers/download-file';
import dayjs from 'dayjs';
import { IoMdSync } from 'react-icons/io';
import { BsFiletypePdf } from 'react-icons/bs';

/**
 * @description Component for rendering deployment history table column with status and action buttons.
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Object} props - Component props
 * @param {CellContext<Deployment, unknown>} props.info - Cell context containing deployment data
 *
 * @returns {JSX.Element} Rendered component
 */
const LastColumn = ({ info }: { info: CellContext<Deployment, unknown> }) => {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const [backUrl, setBackUrl] = useState(pathname);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  /**
   * @description Handles the download of the deployment report as a PDF.
   *
   * @SADtrace SAD-003-6.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   * 10-19-2026 aleksandar dukleski: DLA-1446 download the report of the line's version and report a failure to the user
   *
   * @param {Deployment} deployment - The deployment to download the report for
   *
   * @returns {Promise<void>}
   */
  const handleDownload = async (deployment: Deployment) => {
    setIsDownloadingPDF(true);

    try {
      const pdfReport = await DeploymentService.downloadDrugLibraryReport(
        deployment.id,
        {
          version_major: deployment.version_major,
          version_minor: deployment.version_minor,
        },
      );
      downloadFile(
        pdfReport.data,
        `drug-library-report-${deployment.version_major}.${deployment.version_minor}-${dayjs().format('YYYY-MM-DD')}.pdf`,
        'application/pdf',
      );
      setIsDownloadingPDF(false);
    } catch (e: any) {
      // the backend answers 404 when the report of the line's version cannot be found
      toast.error(
        e?.response?.status === 404
          ? t('deployment.errors.report_not_available')
          : t('errors.default'),
      );
      setIsDownloadingPDF(false);
    }
  };

  useEffect(() => {
    setBackUrl(pathname);
  }, [pathname]);

  const deployment = info.row.original;

  return (
    <div className="text-left text-nowrap w-100">
      <div className="d-flex align-items-center justify-content-start gap-1 btn-controls">
        {deployment.report_filename ? (
          <button
            className={`btn btn-primary me-2 btn-sm`}
            disabled={isDownloadingPDF}
            onClick={() => handleDownload(deployment)}
          >
            {isDownloadingPDF && (
              <IoMdSync size={24} className="rotate-infinute" />
            )}
            {!isDownloadingPDF && (
              <>
                <BsFiletypePdf
                  data-for="report-download-pdf-button"
                  data-tip={t('reports.download_pdf')}
                  size={24}
                />
                <ReactTooltip
                  id="report-download-pdf-button"
                  place="bottom"
                  effect="solid"
                />
              </>
            )}
          </button>
        ) : null}
        {deployment.partial_deployment ? (
          deployment.active ? (
            <>
              <RevertButton backUrl={backUrl} />
            </>
          ) : deployment.partial_reverted ? (
            <b>{t('deployment.table.partial_deployment_reverted')}</b>
          ) : null
        ) : deployment.active ? (
          <div
            data-tip={t('deployment.messages.type__deployment__full')}
            className={'color'}
          >
            {t('deployment.table.currently_deployed')}
          </div>
        ) : deployment.can_revert ? (
          <>
            <Link
              to={`${backUrl}/${DEPLOYMENT_UPDATE_PREFIX}/${deployment.id}`}
              state={backUrl}
              data-tip={t('deployment.buttons.revert_btn')}
              className="icon"
            >
              <TbArrowBack size={26} />
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default LastColumn;

/**
 * @description Component for reverting a partial deployment with version information.
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Object} props - Component props
 * @param {string} props.backUrl - URL to return to after revert action
 *
 * @returns {JSX.Element|null} Rendered component or null if no active deployment exists
 */
const RevertButton = ({ backUrl }: { backUrl: string }) => {
  const { t } = useTranslation();

  const { data: activeFullDeployment, failureReason } = useQuery({
    queryKey: [ReactQueryKeys.DEPLOYMENTS],
    queryFn: () => DeploymentService.getLatestActiveDeployment(),
  });

  // Surface query failures from an effect, not during render — calling toast.error
  // mid-render updates the Toaster while RevertButton is still rendering. failureReason
  // is the actual error object (v5 replacement for the removed useQuery onError).
  useEffect(() => {
    if (failureReason) {
      toast.error(
        (failureReason as any)?.response?.data?.message || t('errors.default'),
      );
    }
  }, [failureReason, t]);

  if (!activeFullDeployment?.data) return null;

  const version = activeFullDeployment?.data?.version_major;

  return (
    <Link
      to={`${backUrl}/${DEPLOYMENT_UPDATE_PARTIAL_PREFIX}/${PARTIAL_UPDATE_TYPE.REVERT}`}
      state={backUrl}
      data-for={activeFullDeployment.data.id}
      data-tip={t('deployment.buttons.revert_partial_btn', {
        version,
      })}
      className="icon"
      data-testid="revert_btn"
    >
      <ReactTooltip
        effect="solid"
        place="bottom"
        className="toolTip"
        id={activeFullDeployment.data.id + ''}
      />
      <FiSkipBack size={26} />
    </Link>
  );
};
